import { SKAG_CREDIT_APPLICATION_FAILURE_REASONS, SKAG_CREDIT_APPLICATION_STATUSES } from 'cpool-constants';
import { schemaId } from 'cpool-schemas';
import { z } from 'zod';

// Each row is one of three variants discriminated by `status`. Fields that
// should not carry data in a given state are typed as `z.null()` (literal)
// rather than omitted, so structural access (`row.creditId`, `row.lastError`)
// stays valid for consumers while illegal combinations
// (SUCCEEDED with lastError set, FAILED with creditId, IN_FLIGHT with
// succeededAt) become unrepresentable at the type level.

const baseFields = {
    attemptCount: z.number().int().nonnegative().default(0),
    loanRequestId: schemaId,
};

export const skagInFlightRowSchema = z.object({
    ...baseFields,
    creditId: z.null().default(null),
    failureReason: z.null().default(null),
    lastError: z.null().default(null),
    status: z.literal(SKAG_CREDIT_APPLICATION_STATUSES.IN_FLIGHT),
    succeededAt: z.null().default(null),
});

export const skagSucceededRowSchema = z.object({
    ...baseFields,
    creditId: z.string(),
    failureReason: z.null().default(null),
    lastError: z.null().default(null),
    status: z.literal(SKAG_CREDIT_APPLICATION_STATUSES.SUCCEEDED),
    succeededAt: z.number(),
});

export const skagFailedRowSchema = z.object({
    ...baseFields,
    creditId: z.null().default(null),
    failureReason: z.nativeEnum(SKAG_CREDIT_APPLICATION_FAILURE_REASONS),
    lastError: z.string().nullable().default(null),
    status: z.literal(SKAG_CREDIT_APPLICATION_STATUSES.FAILED),
    succeededAt: z.null().default(null),
});

export const SkagCreditApplicationsTableDefinitionZod = z.discriminatedUnion('status', [
    skagInFlightRowSchema,
    skagSucceededRowSchema,
    skagFailedRowSchema,
]);

export type SkagCreditApplicationsTableSchema = z.infer<typeof SkagCreditApplicationsTableDefinitionZod>;
export type SkagInFlightRow = z.infer<typeof skagInFlightRowSchema>;
export type SkagSucceededRow = z.infer<typeof skagSucceededRowSchema>;
export type SkagFailedRow = z.infer<typeof skagFailedRowSchema>;
